import * as React from 'react';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../../components/Button';
import GlobalModal from '../../../components/GlobalModal';
import DisplayDateTime from '../../../components/DisplayDateTime';
import ComplaintsProgressModal, { ViewTypes } from './ComplaintsProgressModal';
import LoadingSpinner from '../../../components/LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../../../assets/svg/plus.svg';

// TYPES
import { ComplaintActionsAction } from '../../../api/models';

interface ComplaintsProgressTypes {
	complaintId?: string;
	canAddActions?: boolean;
	canViewActions?: boolean;
	getComplaintsDetailsData: () => Promise<void>;
	complaintProgressData: ComplaintActionsAction[];
	getComplaintsProgress: () => Promise<void>;
	complaintProgressFormErrorMessage?: string;
	isLoading: boolean;
}

export const ComplaintsProgress: React.FC<ComplaintsProgressTypes> = ({
	complaintId,
	canAddActions,
	canViewActions,
	getComplaintsDetailsData,
	complaintProgressData,
	getComplaintsProgress,
	complaintProgressFormErrorMessage,
	isLoading
}) => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Set up Modal States
	const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);

	const [showReadModal, setShowReadModal] = React.useState<boolean>(false);

	const [modalData, setModalData] = React.useState<ComplaintActionsAction | undefined>();

	const createItem = () => {
		setShowReadModal(false);

		setShowCreateModal(true);
	};

	const readItem = (index: number) => {
		const item = complaintProgressData[index];

		setModalData(item);

		setShowCreateModal(false);

		setShowReadModal(true);
	};

	return (
		<aside className="feed-list">
			{!!complaintProgressFormErrorMessage ? (
				<div className="form-validation-error">{complaintProgressFormErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{canViewActions && (
									<>
										<header>
											<h1 className="h4">Complaints progress</h1>

											{isOrganisationActive && canAddActions && (
												<Button className="btn-secondary small" buttonIcon={<PlusIcon />} onClick={createItem}>
													Log action
												</Button>
											)}
										</header>

										<ul>
											{complaintProgressData &&
												complaintProgressData.length > 0 &&
												complaintProgressData.map((item, index) => (
													<li key={item.id}>
														<div>
															<p>{item.title}</p>
															<time>
																<DisplayDateTime>{item.occurrence}</DisplayDateTime>
															</time>
														</div>

														<Button
															className="btn-icon"
															buttonIcon={<PlusIcon />}
															onClick={() => readItem(index)}
														></Button>
													</li>
												))}
										</ul>

										<GlobalModal isOpen={showCreateModal} setIsOpen={setShowCreateModal} titleText="Log an activity">
											<ComplaintsProgressModal
												complaintId={complaintId}
												viewType={ViewTypes.Create}
												refreshComplaintsProgress={getComplaintsProgress}
												getComplaintsDetailsData={getComplaintsDetailsData}
											/>
										</GlobalModal>

										<GlobalModal isOpen={showReadModal} setIsOpen={setShowReadModal} titleText="Complaint progress">
											<ComplaintsProgressModal
												complaintId={complaintId}
												viewType={ViewTypes.Read}
												activity={modalData}
												refreshComplaintsProgress={getComplaintsProgress}
												getComplaintsDetailsData={getComplaintsDetailsData}
											/>
										</GlobalModal>
									</>
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</aside>
	);
};

export default ComplaintsProgress;
